import { supabase } from './client';
import { QuotationPage, PriceScenario } from '@/components/quotation/types';

export interface Quotation {
  id: string;
  title: string;
  client?: string;
  trip_id?: string;
  destination: string[] | string;
  start_date?: string | null;
  end_date?: string | null;
  description?: string;
  notes?: string;
  cover_images?: string[];
  pages: QuotationPage[];
  price_scenarios?: PriceScenario[];
  use_day_mode?: boolean;
  use_page_dropdown?: boolean;
  show_map_lines?: boolean;
  show_accommodations_in_itinerary?: boolean;
  show_price_tab?: boolean;
  show_recommendations_tab?: boolean;
  recommendations_tab_name?: string;
  recommendation_groups?: any[];
  created_at?: string;
  updated_at?: string;
  // Campos novos para o sistema de versionamento
  version?: 'work' | 'published' | 'history';
  is_published?: boolean;
  published_id?: string;
  original_id?: string;
  published_at?: string;
  // Campos adicionais para configurações de visualização
  price_tab_name?: string;
  accommodations_tab_name?: string;
  show_accommodations_tab?: boolean;
  flights_tab_name?: string;
  show_flights_tab?: boolean;
  show_flights_in_itinerary?: boolean;
  flight_groups?: any[];
  use_custom_flight_groups?: boolean;
  flights_description?: string;
  accommodation_groups?: any[];
  use_custom_accommodation_groups?: boolean;
  accommodations_description?: string;
  show_accommodations_map_section?: boolean;
  // === Adicionar campos da Aba Personalizada (formato Supabase) ===
  show_custom_tab?: boolean;
  custom_tab_name?: string;
  custom_tab_groups?: any[]; // Ou CustomTabGroup[] se o tipo for o mesmo
  // ===========================================================
  // Campo para detalhamento de preços
  price_details?: string;
}

export const quotationService = {
  // Buscar todas as cotações
  getQuotations: async (): Promise<Quotation[]> => {
    const { data, error } = await supabase
      .from('quotations')
      .select('*')
      .eq('version', 'work') // Apenas buscar versões de trabalho
      .order('created_at', { ascending: false });
    
    if (error) {
      throw error;
    }
    
    return data || [];
  },
  
  // Buscar cotação por ID
  getQuotationById: async (id: string): Promise<Quotation | null> => {
    const { data, error } = await supabase
      .from('quotations')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) {
      throw error;
    }
    
    return data;
  },
  
  // Buscar cotações por viagem
  getQuotationsByTrip: async (tripId: string): Promise<Quotation[]> => {
    const { data, error } = await supabase
      .from('quotations')
      .select('*')
      .eq('trip_id', tripId)
      .eq('version', 'work') // Apenas buscar versões de trabalho
      .order('created_at', { ascending: false });
    
    if (error) {
      throw error;
    }
    
    return data || [];
  },
  
  // Criar uma nova cotação
  createQuotation: async (quotation: Omit<Quotation, 'id' | 'created_at' | 'updated_at'>): Promise<Quotation> => {
    // Gerar um UUID para a cotação
    const id = crypto.randomUUID();
    
    // Garantir que a estrutura de dados está correta
    const safeQuotation = {
      id,
      ...quotation,
      // Formatar arrays e objects para armazenamento
      destination: Array.isArray(quotation.destination) 
        ? quotation.destination 
        : typeof quotation.destination === 'string'
          ? quotation.destination.split(',').map((d: string) => d.trim()).filter(Boolean)
          : [],
      cover_images: Array.isArray(quotation.cover_images) ? quotation.cover_images : [],
      // Garantir informações de versionamento
      version: quotation.version || 'work',
      is_published: quotation.is_published || false
    };
    
    const { data, error } = await supabase
      .from('quotations')
      .insert([safeQuotation])
      .select()
      .single();
    
    if (error) {
      throw error;
    }
    
    return data;
  },
  
  // Atualizar cotação existente
  updateQuotation: async (id: string, updates: Partial<Omit<Quotation, 'id' | 'created_at' | 'updated_at'>>): Promise<Quotation> => {
    console.log("[quotationService] updateQuotation iniciado com ID:", id);
    console.log("[quotationService] Tipo dos dados de atualização:", typeof updates);
    console.log("[quotationService] Estrutura de atualização:", {
      temPages: Array.isArray(updates.pages),
      qtdPages: updates.pages?.length || 0,
      primeiraPagina: updates.pages?.[0]?.id ? `ID: ${updates.pages[0].id}` : 'Sem páginas',
      ultimaPagina: updates.pages?.length ? `ID: ${updates.pages[updates.pages.length-1].id}` : 'Sem páginas'
    });
    
    // Primeiro, buscar o registro atual para garantir que temos todos os campos
    const { data: existingQuotation, error: fetchError } = await supabase
      .from('quotations')
      .select('*')
      .eq('id', id)
      .single();
    
    if (fetchError) {
      console.error("[quotationService] Erro ao buscar cotação existente:", fetchError);
      throw fetchError;
    }
    
    console.log("[quotationService] Cotação existente encontrada:", !!existingQuotation);
    console.log("[quotationService] Páginas na cotação existente:", {
      temPages: Array.isArray(existingQuotation?.pages),
      qtdPages: existingQuotation?.pages?.length || 0,
      primeiraPagina: existingQuotation?.pages?.[0]?.id ? `ID: ${existingQuotation.pages[0].id}` : 'Sem páginas'
    });
    
    // Certifique-se de que estamos atualizando uma versão de trabalho
    // se não for explicitamente especificado
    const safeUpdates = {
      ...existingQuotation, // Preservar todos os campos existentes
      ...updates, // Sobrescrever com os campos atualizados
      version: updates.version || existingQuotation.version || 'work',
      updated_at: new Date().toISOString(),
    };
    
    console.log("[quotationService] Páginas nas atualizações mescladas:", {
      temPages: Array.isArray(safeUpdates.pages),
      qtdPages: safeUpdates.pages?.length || 0,
      primeiraPagina: safeUpdates.pages?.[0]?.id ? `ID: ${safeUpdates.pages[0].id}` : 'Sem páginas',
      ultimaPagina: safeUpdates.pages?.length ? `ID: ${safeUpdates.pages[safeUpdates.pages.length-1].id}` : 'Sem páginas'
    });
    
    console.log("[quotationService] Verificando objeto de páginas:", {
      isArray: Array.isArray(safeUpdates.pages),
      constructor: safeUpdates.pages?.constructor?.name,
      stringified: JSON.stringify(safeUpdates.pages).substring(0, 100) + '...',
      size: JSON.stringify(safeUpdates.pages).length
    });
    
    // Segurança adicional: preservar campos críticos se estiverem vazios nas atualizações
    if (existingQuotation.notes && (!updates.notes || updates.notes.trim() === '')) {
      console.log(`[quotationService] Preservando campo notes existente durante atualização`, {
        existingNotesLength: existingQuotation.notes.length,
        updateNotesProvided: updates.notes !== undefined,
        updateNotesEmpty: !updates.notes || updates.notes.trim() === '',
        updateNotesValue: updates.notes?.substring(0, 30) || 'não fornecido',
        isHtml: updates.notes?.includes('<') && updates.notes?.includes('>'),
        trimmedLength: updates.notes?.trim().length || 0
      });
      safeUpdates.notes = existingQuotation.notes;
    } else {
      console.log(`[quotationService] O campo notes está sendo atualizado`, {
        existingNotesLength: existingQuotation.notes?.length || 0,
        updateNotesLength: updates.notes?.length || 0,
        updateNotesValue: updates.notes?.substring(0, 30) || 'não fornecido',
        emptyCheck: (!updates.notes || updates.notes.trim() === '') ? 'vazio' : 'não vazio'
      });
    }
    
    console.log("[quotationService] Enviando atualização para o Supabase", {
      method: 'update',
      table: 'quotations',
      id: id,
      dataSize: JSON.stringify(safeUpdates).length
    });
    
    const { data, error } = await supabase
      .from('quotations')
      .update(safeUpdates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      console.error("[quotationService] Erro ao atualizar cotação:", error);
      console.error("[quotationService] Detalhes do erro:", {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint
      });
      throw error;
    }
    
    console.log("[quotationService] Cotação atualizada com sucesso", {
      returnedId: data?.id,
      temPagesNoRetorno: Array.isArray(data?.pages),
      qtdPagesNoRetorno: data?.pages?.length || 0
    });
    return data;
  },
  
  // Excluir cotação
  deleteQuotation: async (id: string): Promise<boolean> => {
    const { error } = await supabase
      .from('quotations')
      .delete()
      .eq('id', id);
    
    if (error) {
      throw error;
    }
    
    return true;
  },
  
  // Migrar cotações do localStorage para o Supabase
  migrateLocalQuotations: async (): Promise<boolean> => {
    try {
      // Obter dados do localStorage
      const localDraft = localStorage.getItem('voah_quotation_draft');
      const localQuotations = localStorage.getItem('voah_quotations');
      
      const quotations = [];
      
      // Processar rascunho atual, se existir
      if (localDraft) {
        try {
          const draftQuotation = JSON.parse(localDraft);
          if (draftQuotation && draftQuotation.id) {
            quotations.push(draftQuotation);
          }
        } catch (e) {
          // Erro silenciado
        }
      }
      
      // Processar cotações salvas, se existirem
      if (localQuotations) {
        try {
          const savedQuotations = JSON.parse(localQuotations);
          if (Array.isArray(savedQuotations) && savedQuotations.length > 0) {
            quotations.push(...savedQuotations);
          }
        } catch (e) {
          // Erro silenciado
        }
      }
      
      if (quotations.length === 0) {
        return false;
      }
      
      // Formatar dados para inserção
      const formattedQuotations = quotations.map(q => ({
        id: q.id,
        title: q.title || '',
        client: q.client || null,
        trip_id: q.tripId || null,
        destination: Array.isArray(q.destination) 
          ? q.destination 
          : typeof q.destination === 'string'
            ? q.destination.split(',').map((d: string) => d.trim()).filter(Boolean)
            : [],
        start_date: q.startDate ? new Date(q.startDate).toISOString() : null,
        end_date: q.endDate ? new Date(q.endDate).toISOString() : null,
        description: q.description || '',
        notes: q.notes || '',
        cover_images: Array.isArray(q.coverImages) ? q.coverImages : [],
        pages: q.pages || [],
        price_scenarios: q.priceScenarios || [],
        use_day_mode: q.useDayMode || false,
        use_page_dropdown: q.usePageDropdown || false,
        show_map_lines: q.showMapLines || true,
        show_accommodations_in_itinerary: q.showAccommodationsInItinerary || true,
        show_price_tab: q.showPriceTab || false,
        show_recommendations_tab: q.showRecommendationsTab || false,
        recommendations_tab_name: q.recommendationsTabName || 'Mais Dicas',
        recommendation_groups: q.recommendationGroups || [],
        // Adicionar informações de versionamento
        version: 'work',
        is_published: false
      }));
      
      // Inserir em lote no Supabase
      for (const quotation of formattedQuotations) {
        const { error } = await supabase
          .from('quotations')
          .upsert([quotation], { 
            onConflict: 'id',
            ignoreDuplicates: false 
          });
        
        if (error) {
          throw error;
        }
      }
      
      return true;
    } catch (error) {
      throw error;
    }
  },
  
  // Obter URL pública para compartilhamento da cotação
  getPublicShareUrl: (id: string): string => {
    return `${window.location.origin}/client/quotations/${id}`;
  }
}; 